/**
 * Google Analytics 4 初始化组件
 * 在 React 应用中正确处理环境变量和用户同意
 */
import { useEffect } from 'react';
import { CONSENT_STORAGE_KEY, hasAnalyticsConsent } from './PrivacyBanner';

export const GoogleAnalytics: React.FC = () => {
  useEffect(() => {
    // 只在生产环境且有 GA ID 时初始化
    const isProduction = import.meta.env.PROD;
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;

    if (!isProduction || !gaId) {
      console.log('[GA] Not loading in development or missing GA ID');
      return;
    }

    // 检查用户同意
    const hasConsent = hasAnalyticsConsent();

    if (hasConsent) {
      // 用户已同意，加载 GA
      loadGoogleAnalytics(gaId);
    } else {
      // 用户未同意，初始化同意模式
      initializeConsentMode();
    }

    // 监听同意状态变化
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CONSENT_STORAGE_KEY) {
        const newConsent = hasAnalyticsConsent();
        if (newConsent && !window.gtag) {
          // 用户刚刚同意，加载 GA
          loadGoogleAnalytics(gaId);
        } else if (newConsent && window.gtag) {
          // 用户同意且 GA 已加载，更新同意状态
          window.gtag('consent', 'update', {
            analytics_storage: 'granted',
          });
        } else if (!newConsent && window.gtag) {
          // 用户撤销同意
          window.gtag('consent', 'update', {
            analytics_storage: 'denied',
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return null;
};

/**
 * 加载 Google Analytics
 */
function loadGoogleAnalytics(gaId: string) {
  // 检查是否已经加载
  if (window.gtag) {
    console.log('[GA] Already loaded');
    return;
  }

  console.log('[GA] Loading Google Analytics with consent');

  // 创建并加载 gtag 脚本
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  // 初始化 gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }

  gtag('js', new Date());

  // 配置 GA 隐私设置
  gtag('config', gaId, {
    // 隐私友好设置
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    // Cookie 设置
    cookie_flags: 'SameSite=Strict;Secure',
    cookie_expires: 60 * 60 * 24 * 30, // 30天
    // 尊重 Do Not Track
    send_page_view: !navigator.doNotTrack || navigator.doNotTrack !== '1',
    // GDPR 合规
    consent_mode: {
      analytics_storage: 'granted',
      ad_storage: 'denied',
    },
  });

  // 全局可用
  window.gtag = gtag;
}

/**
 * 初始化同意模式（用户未同意时）
 */
function initializeConsentMode() {
  console.log('[GA] Initializing consent mode (denied)');

  // 只初始化 dataLayer 和基础 gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }

  // 设置默认同意状态为拒绝
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
  });

  window.gtag = gtag;
}
